import type { LessonFields } from "@/types/Lesson";
import type { ProjectFields } from "@/types/Project";
import type { SlideFields } from "@/types/Slide";
import { type PayloadAction, createSlice } from "@reduxjs/toolkit";

export interface UiState {
  isEditProjectModalOpen: boolean;
  editingProject: ProjectFields | null;

  isEditLessonModalOpen: boolean;
  editingLesson: LessonFields | null;

  isEditSlideModalOpen: boolean;
  editingSlide: SlideFields | null;
  // Set while the add-slide modal is creating a subslide of this main slide.
  subslideParent: { id: string; name: string } | null;
  // Set while picking the main slide the chosen slides should become subslides of.
  nestingSlides: { ids: string[]; label: string } | null;
}

const initialState: UiState = {
  isEditProjectModalOpen: false,
  editingProject: null,

  isEditLessonModalOpen: false,
  editingLesson: null,

  isEditSlideModalOpen: false,
  editingSlide: null,
  subslideParent: null,
  nestingSlides: null,
};

/**
 * UI slice for managing application UI state
 */
const uiSlice = createSlice({
  name: "ui",
  initialState,
  reducers: {
    /* ---------- Project ---------- */
    openEditProjectModal: (state, action: PayloadAction<ProjectFields>) => {
      state.isEditProjectModalOpen = true;
      state.editingProject = action.payload;
    },
    closeEditProjectModal: (state) => {
      state.isEditProjectModalOpen = false;
      state.editingProject = null;
    },

    /* ---------- Lesson ---------- */
    openEditLessonModal: (state, action: PayloadAction<LessonFields>) => {
      state.isEditLessonModalOpen = true;
      state.editingLesson = action.payload;
    },
    closeEditLessonModal: (state) => {
      state.isEditLessonModalOpen = false;
      state.editingLesson = null;
    },

    /* ---------- Slide ---------- */
    openEditSlideModal: (state, action: PayloadAction<SlideFields | null>) => {
      state.isEditSlideModalOpen = true;
      state.editingSlide = action.payload;
      state.subslideParent = null;
    },
    openAddSubslideModal: (state, action: PayloadAction<{ id: string; name: string }>) => {
      state.isEditSlideModalOpen = true;
      state.editingSlide = null;
      state.subslideParent = action.payload;
    },
    openNestSlideModal: (state, action: PayloadAction<{ ids: string[]; label: string }>) => {
      state.nestingSlides = action.payload;
    },
    closeNestSlideModal: (state) => {
      state.nestingSlides = null;
    },
    closeEditSlideModal: (state) => {
      state.isEditSlideModalOpen = false;
      state.editingSlide = null;
      state.subslideParent = null;
    },
  },
});

export const {
  openEditProjectModal,
  closeEditProjectModal,
  openEditLessonModal,
  closeEditLessonModal,
  openEditSlideModal,
  openAddSubslideModal,
  openNestSlideModal,
  closeNestSlideModal,
  closeEditSlideModal,
} = uiSlice.actions;

export default uiSlice.reducer;
